import { useEffect } from "react";
import { ComparisonTable } from "../components/landing/ComparisonTable";
import { FAQSection } from "../components/landing/FAQSection";
import { GuidesSection } from "../components/landing/GuidesSection";
import { FinalCTA } from "../components/landing/FinalCTA";
import { Footer } from "../components/landing/Footer";
import { Header } from "../components/landing/Header";
import { HeroSection } from "../components/landing/HeroSection";
import { HowItWorks } from "../components/landing/HowItWorks";
import { MetricsStrip } from "../components/landing/MetricsStrip";
import { SocialIntentSection } from "../components/landing/SocialIntentSection";
import { StickySearchBar } from "../components/landing/StickySearchBar";
import { TrendingSection } from "../components/landing/TrendingSection";
import {
  getLandingLocale,
  landingPaths,
} from "../data/guideAlternates";
import { landingContent } from "../data/landingContent";
import { getLocale } from "../i18n";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { useLocation } from "react-router-dom";

function setMeta(
  selector: string,
  attribute: "content" | "href",
  value: string,
) {
  const element = document.head.querySelector(selector);
  if (element) {
    element.setAttribute(attribute, value);
  }
}

function upsertJsonLd(id: string, schema: object) {
  let script = document.getElementById(id) as HTMLScriptElement | null;

  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify(schema);
}

function useLandingSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const { seo, faq } = landingContent;
    const locale = getLandingLocale(pathname) ?? getLocale();
    const canonical = `https://stalkie.app${landingPaths[locale]}`;

    document.title = seo.title;
    document.documentElement.lang = locale === "pt" ? "pt-BR" : locale;
    setMeta('meta[name="description"]', "content", seo.description);
    setMeta('link[rel="canonical"]', "href", canonical);
    setMeta('meta[property="og:title"]', "content", seo.ogTitle);
    setMeta('meta[property="og:description"]', "content", seo.ogDescription);
    setMeta(
      'meta[property="og:locale"]',
      "content",
      locale === "pt" ? "pt_BR" : locale === "es" ? "es_ES" : "en_US",
    );
    setMeta('meta[property="og:image"]', "content", seo.ogImage);
    setMeta('meta[property="og:url"]', "content", canonical);
    setMeta('meta[name="twitter:card"]', "content", seo.twitterCard);
    setMeta('meta[name="twitter:title"]', "content", seo.ogTitle);
    setMeta('meta[name="twitter:description"]', "content", seo.ogDescription);
    setMeta('meta[name="twitter:image"]', "content", seo.ogImage);

    document.head
      .querySelectorAll('link[rel="alternate"]')
      .forEach((element) => element.remove());
    const alternateLocales = [
      ["en", "en"],
      ["pt-BR", "pt"],
      ["es", "es"],
    ] as const;
    alternateLocales.forEach(([hreflang, alternateLocale]) => {
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = hreflang;
      link.href = `https://stalkie.app${landingPaths[alternateLocale]}`;
      link.dataset.landingHreflang = "true";
      document.head.appendChild(link);
    });
    const defaultLink = document.createElement("link");
    defaultLink.rel = "alternate";
    defaultLink.hreflang = "x-default";
    defaultLink.href = "https://stalkie.app/";
    defaultLink.dataset.landingHreflang = "true";
    document.head.appendChild(defaultLink);

    upsertJsonLd("stalkie-software-schema", {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Stalkie",
      applicationCategory: "SocialNetworkingApplication",
      operatingSystem: "Web",
      url: canonical,
      description: seo.description,
    });

    upsertJsonLd("stalkie-faq-schema", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.items.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    });
  }, [pathname]);
}

export function LandingPage() {
  useLandingSeo();
  useScrollReveal();

  return (
    <div className="st-page">
      <Header />
      <main>
        <HeroSection />
        <MetricsStrip />
        <HowItWorks />
        <TrendingSection />
        <SocialIntentSection />
        <ComparisonTable />
        <FAQSection />
        <GuidesSection />
        <FinalCTA />
      </main>
      <Footer />
      <StickySearchBar />
    </div>
  );
}
